import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSession } from "../session";
import { Badge, Card, Empty, ErrorBox, Loading, Notice, PageHeader } from "../components/ui";
import { IconPlus } from "../components/icons";
import { dateTime, num, stateWord } from "../lib/format";

interface IntegrationsResponse {
  providers: { key: string; title: string; description: string; configured: boolean; status: string }[];
  integrations: {
    id: number;
    provider: string;
    scope_key: string;
    display_name: string | null;
    key_suffix: string | null;
    status: string;
    last_ok_at: string | null;
    last_error: string | null;
  }[];
}

interface TokensResponse {
  tokens: {
    id: number;
    name: string;
    prefix: string;
    scope: string;
    created_at: string;
    last_used_at: string | null;
    revoked: boolean;
  }[];
}

interface MembersResponse {
  members: {
    user_id: number;
    email: string;
    full_name: string;
    role: string;
    role_title: string;
    last_login_at: string | null;
  }[];
  roles: { key: string; title: string }[];
}

export default function SettingsPage() {
  const { channel, channelId, can, refresh } = useSession();
  const queryClient = useQueryClient();
  const [tokenName, setTokenName] = useState("Tokel");
  const [issued, setIssued] = useState<string | null>(null);
  const [invite, setInvite] = useState({ email: "", role: "viewer" });
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const integrations = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get<IntegrationsResponse>("/integrations"),
  });
  const tokens = useQuery({
    queryKey: ["tokens"],
    queryFn: () => api.get<TokensResponse>("/integrations/tokens"),
    enabled: can("manage_integrations"),
  });
  const members = useQuery({
    queryKey: ["members"],
    queryFn: () => api.get<MembersResponse>("/auth/members"),
  });

  const saveChannel = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/channels/${channelId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channels"] });
      refresh();
    },
  });
  const check = useMutation({
    mutationFn: (provider: string) =>
      api.post<{ ok: boolean; message: string }>(
        `/integrations/${provider}/check${channelId ? `?channel_id=${channelId}` : ""}`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["integrations"] }),
  });
  const createToken = useMutation({
    mutationFn: () => api.post<{ token: string }>("/integrations/tokens", { name: tokenName }),
    onSuccess: (data) => {
      setIssued(data.token);
      queryClient.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
  const revokeToken = useMutation({
    mutationFn: (id: number) => api.del(`/integrations/tokens/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tokens"] }),
  });
  const inviteMember = useMutation({
    mutationFn: () => api.post<{ invite_link: string | null }>("/auth/members", invite),
    onSuccess: (data) => {
      setInviteLink(data.invite_link);
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  return (
    <>
      <PageHeader
        title="Настройки"
        lede="Канал, внешние сервисы, машинные пропуска и люди в рабочем пространстве."
      />

      {channel ? (
        <Card title={`Канал «${channel.title}»`}>
          <div className="grid two">
            <div className="field">
              <label htmlFor="tz">Часовой пояс</label>
              <input
                id="tz"
                defaultValue={channel.timezone}
                onBlur={(event) => saveChannel.mutate({ timezone: event.target.value })}
              />
              <span className="hint">Сутки и часы показываются в нём.</span>
            </div>
            <div className="field">
              <label htmlFor="maturity">Срок дозревания, часов</label>
              <input
                id="maturity"
                type="number"
                min={0}
                defaultValue={channel.maturity_is_manual ? channel.maturity_hours : 0}
                onBlur={(event) => saveChannel.mutate({ maturity_hours: Number(event.target.value) })}
              />
              <span className="hint">
                0 — определять по данным канала. Сейчас используется {channel.maturity_hours} ч
                {channel.maturity_detected_hours ? ` (определено: ${channel.maturity_detected_hours} ч)` : ""}.
              </span>
            </div>
          </div>

          <div className="field">
            <label>
              <input
                type="checkbox"
                style={{ width: "auto", marginRight: 8 }}
                defaultChecked={channel.has_admin_rights}
                onChange={(event) => saveChannel.mutate({ has_admin_rights: event.target.checked })}
              />
              У нашего аккаунта есть права администратора в канале
            </label>
            <span className="hint">
              От этого зависит половина возможностей: точная атрибуция закупов по
              ссылкам-приглашениям и число удержанных подписчиков.
            </span>
          </div>

          <div className="pill-row">
            {(
              [
                ["track_topics", "Рубрики", channel.track.topics],
                ["track_ads", "Закупы", channel.track.ads],
                ["track_competitors", "Конкуренты", channel.track.competitors],
                ["track_experiments", "Эксперименты", channel.track.experiments],
              ] as const
            ).map(([key, label, value]) => (
              <label key={key} style={{ display: "inline-flex", gap: 6, alignItems: "center", marginRight: 14 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  defaultChecked={value}
                  onChange={(event) => saveChannel.mutate({ [key]: event.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>
          <p className="faint">
            Выключенный блок не считается и не стоит денег — это не «спрятать карточку».
          </p>
          {saveChannel.error && <ErrorBox error={saveChannel.error} />}
        </Card>
      ) : (
        <Empty title="Канал не выбран" />
      )}

      <Card title="Внешние сервисы">
        {integrations.isLoading ? (
          <Loading rows={3} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Сервис</th>
                  <th>Зачем</th>
                  <th>Состояние</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {integrations.data?.providers.map((provider) => (
                  <tr key={provider.key}>
                    <td>{provider.title}</td>
                    <td className="dim">{provider.description}</td>
                    <td>
                      <Badge tone={provider.status === "ok" ? "ok" : provider.status === "error" ? "alert" : "muted"}>
                        {provider.status === "ok"
                          ? "работает"
                          : provider.status === "error"
                            ? "ошибка"
                            : "не настроен"}
                      </Badge>
                    </td>
                    <td>
                      {can("manage_integrations") && (
                        <button className="small" onClick={() => check.mutate(provider.key)} disabled={check.isPending}>
                          Проверить
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {check.data &&
          (check.data.ok ? (
            <Notice>{check.data.message}</Notice>
          ) : (
            <ErrorBox error={check.data.message} />
          ))}
        <p className="faint">
          Проверка делается настоящим вызовом, а не сравнением строк: «ключ похож на ключ»
          зеленеет и на отозванном токене.
        </p>
      </Card>

      {can("manage_integrations") && (
        <Card title="Машинные пропуска" note="ими ходит Tokel за планом эксперимента">
          <div className="row" style={{ marginBottom: 12 }}>
            <input
              value={tokenName}
              onChange={(event) => setTokenName(event.target.value)}
              style={{ maxWidth: 220 }}
            />
            <button className="primary small" onClick={() => createToken.mutate()}>
              <IconPlus size={14} />
              Выдать
            </button>
          </div>
          {issued && (
            <Notice tone="warn">
              Сохраните — показывается один раз: <span className="mono">{issued}</span>
            </Notice>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Начало</th>
                  <th>Последнее использование</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.data?.tokens.map((token) => (
                  <tr key={token.id}>
                    <td>
                      {token.name} {token.revoked && <Badge tone="muted">отозван</Badge>}
                    </td>
                    <td className="mono">{token.prefix}…</td>
                    <td className="dim">{token.last_used_at ? dateTime(token.last_used_at) : "не использовался"}</td>
                    <td>
                      {!token.revoked && (
                        <button className="small" onClick={() => revokeToken.mutate(token.id)}>
                          Отозвать
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Люди в пространстве">
        {members.isLoading ? (
          <Loading rows={2} />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Человек</th>
                    <th>Роль</th>
                    <th>Последний вход</th>
                  </tr>
                </thead>
                <tbody>
                  {members.data?.members.map((member) => (
                    <tr key={member.user_id}>
                      <td>
                        {member.full_name || member.email}
                        <div className="faint">{member.email}</div>
                      </td>
                      <td>{member.role_title}</td>
                      <td className="dim">{member.last_login_at ? dateTime(member.last_login_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {can("manage_members") && (
              <div className="row" style={{ marginTop: 12 }}>
                <input
                  placeholder="почта"
                  value={invite.email}
                  onChange={(event) => setInvite({ ...invite, email: event.target.value })}
                  style={{ maxWidth: 240 }}
                />
                <select
                  value={invite.role}
                  onChange={(event) => setInvite({ ...invite, role: event.target.value })}
                  style={{ maxWidth: 190 }}
                >
                  {members.data?.roles.map((role) => (
                    <option key={role.key} value={role.key}>
                      {role.title}
                    </option>
                  ))}
                </select>
                <button className="primary small" onClick={() => inviteMember.mutate()} disabled={!invite.email}>
                  Пригласить
                </button>
              </div>
            )}
            {inviteLink && (
              <Notice>
                Почтового сервера нет — передайте ссылку сами: <span className="mono">{inviteLink}</span>
              </Notice>
            )}
            {inviteMember.error && <ErrorBox error={inviteMember.error} />}
          </>
        )}
      </Card>

      <Card title="Фоновые задачи">
        <JobRuns channelId={channelId} canRun={can("run_jobs")} />
      </Card>
    </>
  );
}

function JobRuns({ channelId, canRun }: { channelId: number | null; canRun: boolean }) {
  const queryClient = useQueryClient();
  const runs = useQuery({
    queryKey: ["job-runs", channelId],
    queryFn: () => api.get<{ runs: { id: number; job: string; title: string; status: string; started_at: string; duration_ms: number | null; error: string | null }[] }>(
      `/jobs/runs${channelId ? `?channel_id=${channelId}` : ""}`,
    ),
  });
  const run = useMutation({
    mutationFn: (key: string) => api.post(`/jobs/run/${key}${channelId ? `?channel_id=${channelId}` : ""}`),
    onSuccess: () => setTimeout(() => queryClient.invalidateQueries({ queryKey: ["job-runs"] }), 4000),
  });

  return (
    <>
      {canRun && (
        <div className="row" style={{ marginBottom: 12 }}>
          <button className="small" onClick={() => run.mutate("ingest")}>Собрать ленту</button>
          <button className="small" onClick={() => run.mutate("daily")}>Дневной пересчёт</button>
          <button className="small" onClick={() => run.mutate("weekly")}>Недельный пересчёт</button>
          <button className="small" onClick={() => run.mutate("backfill")}>Добрать историю</button>
        </div>
      )}
      {runs.isLoading ? (
        <Loading rows={2} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Задача</th>
                <th>Исход</th>
                <th>Когда</th>
                <th className="num">мс</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.runs.slice(0, 25).map((item) => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>
                    <Badge tone={item.status === "ok" ? "ok" : item.status === "error" ? "alert" : "muted"}>
                      {stateWord(item.status)}
                    </Badge>
                    {item.error && <div className="faint">{item.error.slice(0, 160)}</div>}
                  </td>
                  <td className="dim">{dateTime(item.started_at)}</td>
                  <td className="num mono">{num(item.duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
